import { useEffect, useState } from 'react';
import { Plus, Minus, Banknote, UserRound, Landmark, HandCoins, Check, Camera } from 'lucide-react';
import BottomSheet from './BottomSheet';

/**
 * Vklad / výdej hotovosti na hlavní pokladně — ruční pohyby, které nevznikají
 * samy. Směr určuje tlačítko, kterým člověk přišel (Vklad / Výdej), takže se
 * nabízí jen to, co v tom směru dává smysl:
 *
 *   Vklad:  vklad majitele · z banky
 *   Výdej:  záloha ze mzdy · výběr majitele · do banky
 *
 * Dřív tu byl rozbalovací seznam šesti typů s plusy i mínusy dohromady — ve
 * vkladu šlo vybrat „Záloha mzdy (−)". Za tři měsíce přes něj přitom nikdo
 * nezadal jediný pohyb: zálohy při uzavření směny, výplaty dýška, tržby
 * i nákupy se zapisují samy. Zbyly jen pohyby, které za člověka nikdo neudělá.
 *
 *  - **Nákup s dokladem** tu není — na to je „Vyfoť účtenku" (sken doplní
 *    částku a pozná i tankování).
 *  - **Záloha ze mzdy** chce zaměstnance: bez něj by se nedala odečíst.
 *    Ruční záloha nevisí na směně, proto ji export docházky pro účetní
 *    přidává zvlášť (DochazkaUzavirkaService).
 *  - **Rozvozová pokladna** ruční pohyby nemá — hotovost na ní hýbou jen
 *    objednávky, účtenky a vyúčtování. Plocha pokladny tam tlačítka vůbec
 *    nenabízí.
 *
 * Backend: POST /api/finance/pokladna/smeny/{smenaId}/pohyb
 */
const VOLBY = {
    vklad: [
        { typ: 'vklad_majitel', nazev: 'Vklad majitele', popis: 'Majitel dává peníze do pokladny.', Ikona: UserRound },
        { typ: 'prevod_z_banky', nazev: 'Z banky', popis: 'Hotovost vybraná z účtu a vložená do pokladny.', Ikona: Landmark },
    ],
    vydej: [
        { typ: 'zaloha_mzdy', nazev: 'Záloha ze mzdy', popis: 'Zaměstnanci na ruku — odečte se mu z výplaty.', Ikona: HandCoins, zamestnanec: true },
        { typ: 'vyber_majitel', nazev: 'Výběr majitele', popis: 'Majitel si bere peníze z pokladny.', Ikona: UserRound },
        { typ: 'odvod_do_banky', nazev: 'Do banky', popis: 'Hotovost odnesená a vložená na účet.', Ikona: Landmark },
    ],
};

const POZNAMKA_NAPOVEDA = {
    zaloha_mzdy: 'Např. záloha na září',
    prevod_z_banky: 'Např. výběr z bankomatu, číslo dokladu',
    odvod_do_banky: 'Např. vklad na účet, číslo dokladu',
};

const fmtKc = (n) => Math.round(Number(n)).toLocaleString('cs-CZ') + ' Kč';

export default function PohybSheet({
    open,
    onClose,
    onSaved,
    onVyfotitUctenku,          // výdej: odkaz na „Vyfoť účtenku" pro nákupy
    pokladnaSmena,             // { id, typ, nazev, ... }
    smer = 'vklad',            // 'vklad' | 'vydej' — podle tlačítka, kterým člověk přišel
    isDark,
    isMobile,
}) {
    const isVklad = smer !== 'vydej';
    const jeHlavni = pokladnaSmena?.typ === 'bistro_hlavni';
    const volby = jeHlavni ? VOLBY[isVklad ? 'vklad' : 'vydej'] : [];

    const [typ, setTyp] = useState(volby[0]?.typ ?? null);
    const [castka, setCastka] = useState('');
    const [zamestnanecId, setZamestnanecId] = useState('');
    const [poznamka, setPoznamka] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [zamestnanci, setZamestnanci] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setTyp(volby[0]?.typ ?? null);
        setCastka(''); setZamestnanecId(''); setPoznamka(''); setError(null);
    }, [open, smer]); // eslint-disable-line react-hooks/exhaustive-deps

    const volba = volby.find(v => v.typ === typ) || null;

    useEffect(() => {
        if (!open || !volba?.zamestnanec || zamestnanci.length) return;
        fetch('/api/zamestnanci')
            .then(r => (r.ok ? r.json() : []))
            .then(d => setZamestnanci(Array.isArray(d) ? d : (d?.data ?? [])))
            .catch(() => {});
    }, [open, volba?.zamestnanec, zamestnanci.length]);

    const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

    const handleSubmit = async () => {
        setError(null);
        const c = Number(String(castka).replace(',', '.'));
        if (!volba) { setError('Vyber, o jaký pohyb jde.'); return; }
        if (!c || c <= 0) { setError('Zadej částku větší než 0.'); return; }
        if (volba.zamestnanec && !zamestnanecId) { setError('Vyber zaměstnance — bez něj se záloha nedá odečíst z výplaty.'); return; }
        if (!pokladnaSmena?.id) { setError('Chybí pokladna.'); return; }
        setSubmitting(true);
        try {
            const r = await fetch(`/api/finance/pokladna/smeny/${pokladnaSmena.id}/pohyb`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf(), Accept: 'application/json' },
                body: JSON.stringify({
                    typ: volba.typ,
                    castka_kc: c,
                    zamestnanec_id: volba.zamestnanec ? Number(zamestnanecId) : null,
                    poznamka: poznamka || null,
                }),
            });
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                setError(data.error || data.message || `Uložení selhalo (HTTP ${r.status}).`);
                return;
            }
            onSaved?.();
        } catch (e) {
            setError('Uložení selhalo: ' + (e?.message || 'neznámá chyba'));
        } finally {
            setSubmitting(false);
        }
    };

    const accent = isVklad ? '#10B981' : '#EF4444';
    const labelMuted = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    const textPrimary = isDark ? '#fff' : 'var(--text-primary)';
    const textMuted = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-muted)';
    const inputBg = isDark ? 'rgba(0,0,0,0.30)' : 'var(--surface-base)';
    const inputBorder = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';
    const inputStyle = {
        width: '100%', padding: '10px 12px', borderRadius: 9, fontSize: 14,
        background: inputBg, color: textPrimary,
        border: `1px solid ${inputBorder}`,
        boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit',
    };
    const cislo = Number(String(castka).replace(',', '.'));

    return (
        <BottomSheet open={open} onClose={onClose} isDark={isDark} isMobile={isMobile} title={null}>
            {/* Hlavička */}
            <div style={{ padding: '14px 18px 12px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{
                    width: 44, height: 44, borderRadius: 10,
                    background: `${accent}1A`, color: accent,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                    {isVklad ? <Plus size={22} /> : <Minus size={22} />}
                </span>
                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 18, fontWeight: 800, color: textPrimary }}>
                        {isVklad ? 'Vklad do pokladny' : 'Výdej z pokladny'}
                    </span>
                    <span style={{ fontSize: 12, color: labelMuted, lineHeight: 1.2 }}>
                        <Banknote size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
                        {pokladnaSmena?.nazev || '—'}
                    </span>
                </div>
            </div>

            <div style={{ padding: '0 18px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                {!jeHlavni ? (
                    <div style={{ fontSize: 13.5, color: textMuted, lineHeight: 1.45, padding: '4px 2px 8px' }}>
                        Na rozvozové pokladně se hotovost hýbe jen objednávkami, účtenkami
                        a vyúčtováním. Ruční vklad ani výdej tu není potřeba.
                    </div>
                ) : (
                    <>
                        {/* Co to je — velké volby místo rozbalovacího seznamu */}
                        <div role="radiogroup" aria-label={isVklad ? 'Druh vkladu' : 'Druh výdeje'}
                            style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {volby.map(v => {
                                const vybrano = v.typ === typ;
                                return (
                                    <button key={v.typ} type="button" role="radio" aria-checked={vybrano}
                                        onClick={() => { setTyp(v.typ); setError(null); }}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
                                            padding: '11px 12px', borderRadius: 12, cursor: 'pointer',
                                            fontFamily: 'inherit',
                                            background: vybrano ? `${accent}1A` : inputBg,
                                            border: `1.5px solid ${vybrano ? accent : inputBorder}`,
                                            WebkitTapHighlightColor: 'transparent',
                                        }}>
                                        <span style={{
                                            width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            background: vybrano ? `${accent}2E` : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'),
                                            color: vybrano ? accent : labelMuted,
                                        }}><v.Ikona size={18} /></span>
                                        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <span style={{ fontSize: 14.5, fontWeight: 700, color: textPrimary }}>{v.nazev}</span>
                                            <span style={{ fontSize: 12, color: textMuted, lineHeight: 1.35 }}>{v.popis}</span>
                                        </span>
                                        {vybrano && <Check size={18} style={{ color: accent, flexShrink: 0 }} />}
                                    </button>
                                );
                            })}
                        </div>

                        {volba?.zamestnanec && (
                            <Field label="Komu" labelMuted={labelMuted} required>
                                <select value={zamestnanecId} onChange={e => { setZamestnanecId(e.target.value); setError(null); }} style={inputStyle}>
                                    <option value="">— vyber zaměstnance —</option>
                                    {zamestnanci.map(z => (
                                        <option key={z.id} value={z.id}>
                                            {[z.jmeno, z.prijmeni].filter(Boolean).join(' ') || z.cele_jmeno || `#${z.id}`}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                        )}

                        <Field label="Částka" labelMuted={labelMuted} required>
                            <input type="number" inputMode="decimal" step="1" min="1"
                                value={castka} onChange={e => { setCastka(e.target.value); setError(null); }}
                                placeholder="0 Kč"
                                style={{ ...inputStyle, fontSize: 20, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }} />
                        </Field>

                        <Field label="Poznámka" labelMuted={labelMuted}>
                            <input type="text" value={poznamka} onChange={e => setPoznamka(e.target.value)}
                                placeholder={POZNAMKA_NAPOVEDA[typ] || 'Volitelně…'}
                                style={inputStyle} />
                        </Field>

                        {!isVklad && onVyfotitUctenku && (
                            <button type="button" onClick={onVyfotitUctenku}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 8, padding: '2px 2px',
                                    border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit',
                                    fontSize: 12.5, color: textMuted, textAlign: 'left',
                                }}>
                                <Camera size={14} style={{ color: '#0CC0DF', flexShrink: 0 }} />
                                <span>Platíš nákup? Ten patří do <b style={{ color: '#0CC0DF' }}>Vyfoť účtenku</b> — sken doplní částku sám.</span>
                            </button>
                        )}
                    </>
                )}

                {error && (
                    <div style={{
                        padding: '8px 12px', borderRadius: 8, fontSize: 12.5,
                        background: 'rgba(239,68,68,0.12)', color: '#ef4444',
                        border: '1px solid rgba(239,68,68,0.3)',
                    }}>{error}</div>
                )}

                <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                    <button type="button" onClick={onClose}
                        style={{
                            flex: 1, padding: '12px', borderRadius: 10,
                            background: 'transparent', color: labelMuted,
                            border: `1px solid ${inputBorder}`,
                            fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                        }}>
                        {jeHlavni ? 'Zrušit' : 'Zavřít'}
                    </button>
                    {jeHlavni && (
                        <button type="button" onClick={handleSubmit} disabled={submitting}
                            style={{
                                flex: 2, padding: '12px', borderRadius: 10,
                                background: `linear-gradient(135deg, ${accent} 0%, ${accent}CC 100%)`,
                                color: '#fff', border: 'none', fontFamily: 'inherit',
                                fontSize: 14, fontWeight: 800, letterSpacing: 0.1,
                                cursor: submitting ? 'wait' : 'pointer',
                                opacity: submitting ? 0.5 : 1,
                            }}>
                            {submitting ? 'Ukládám…'
                                : `${isVklad ? 'Vložit' : 'Vydat'}${cislo > 0 ? ` ${fmtKc(cislo)}` : ''}`}
                        </button>
                    )}
                </div>
            </div>
        </BottomSheet>
    );
}

function Field({ label, children, labelMuted, required }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                textTransform: 'uppercase', color: labelMuted,
            }}>{label}{required && <span style={{ color: '#EF4444' }}> *</span>}</span>
            {children}
        </label>
    );
}
